'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { FeedList, useCommunityFeed, useCommunityInteraction } from '@/app/components/shared/Community';
import { TemplateCard } from '@/app/components/shared/TemplateCard';
import { Button } from '@/app/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from '@/app/components/ui/drawer';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { CommunityEnum } from '@/app/types';
import { cn, formatCountForTemplate } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { templatesGet } from './apis';
import type { ViduV1Template } from './types';

export interface TemplateDetailDrawerProps {
  open?: boolean;
  onClose?: () => void;
  scene: string;
  title?: string;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onClickCreate: () => void;
}

export const TemplateDetailDrawer: FC<TemplateDetailDrawerProps> = ({
  open,
  onClose,
  scene,
  title,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onClickCreate,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const [template, setTemplate] = useState<ViduV1Template | null>(null);
  const scrollContainerId = useId();

  const syncFeedDetailConfig = useStore((state) => state.syncFeedDetailConfig);

  const { run: fetchTemplate, loading: templateLoading } = useFetch(
    () =>
      templatesGet({
        path: { scene },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) setTemplate(data);
      },
    },
  );

  const portraitResource = useMemo(() => {
    return template?.creations?.find((c) => c.name === 'portrait') || null;
  }, [template]);

  const [hasMore, setHasMore] = useState(false);
  const pageToken = useRef('');
  const creationSerialId = useRef(-1);

  const {
    getData: getFeedData,
    handleDetail,
    updateInteractionStatus,
    init,
    inspAssets,
    loading,
  } = useCommunityFeed({
    handleEnter: () => {},
    pageToken,
    updatePageInfo: ({ token }) => {
      pageToken.current = token || '';
      setHasMore(!!token);
    },
    updateCreationSerialId: (num) => {
      creationSerialId.current = num;
    },
    activeType: undefined,
    exploreTab: CommunityEnum.MediaAsset,
    mixed: false,
    queryOverrides: {
      scene,
      sort_type: 'recommended',
      channel_type: 'media_asset',
    },
  });

  const { vote, unVote, collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: ({ id, obj_type, voted, collected }) =>
      updateInteractionStatus({ id, obj_type: obj_type as CommunityEnum, voted, collected }),
  });

  const commonCommunityProps = useMemo(() => {
    return {
      handleVote: vote,
      handleUnVote: unVote,
      handleCollect: collect,
      handleUnCollect: unCollect,
    };
  }, [vote, unVote, collect, unCollect]);

  useEffect(() => {
    syncFeedDetailConfig({
      ...commonCommunityProps,
      thumbScrollListConfig: {
        list: inspAssets,
        hasMore,
        onLoadMore: getFeedData,
      },
    });
  }, [inspAssets, hasMore]);

  useEffect(() => {
    if (open && scene) {
      pageToken.current = '';
      init();
      setTemplate(null);
      fetchTemplate();
      getFeedData();
    }
  }, [open, scene]);

  if (!scene) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose?.();
      }}
    >
      <DrawerContent
        className="h-[calc(100vh-32px)] w-full max-w-full bg-[#1A1C20] p-0 data-[vaul-drawer-direction=bottom]:max-h-screen md:p-0"
        aria-describedby={undefined}
      >
        {/* 没有 Title 会报错 */}
        <DrawerTitle className="hidden" />
        <div id={scrollContainerId} className="h-full overflow-auto">
          <div className="flex h-full flex-col p-12 sm:p-3 md:p-8">
            <div className="relative flex items-center justify-center gap-16">
              <img
                src={portraitResource?.resource?.cover_uri}
                alt=""
                className={cn('-z-10 absolute inset-0 h-full w-full object-cover opacity-30 blur-[100px] sm:hidden')}
              />
              <div className="sm:-z-10 relative aspect-[3/4] w-[300px] sm:absolute sm:inset-0 sm:w-full lg:w-[280px]">
                {templateLoading ? (
                  <div className="size-full animate-pulse rounded-12 bg-system-text05" />
                ) : (
                  portraitResource?.resource && (
                    <TemplateCard
                      resource={portraitResource?.resource}
                      variant="portrait"
                      title=""
                      tag={template?.tag}
                      imgClassName="cursor-default"
                      isImageType={template?.type === 'image'}
                    />
                  )
                )}
                <div className="invisible absolute top-80 right-0 bottom-0 left-0 w-full bg-gradient-to-t from-[#1A1C20] to-transparent sm:visible" />
              </div>
              <div className="flex min-w-[240px] flex-col justify-between gap-6 sm:pt-80 sm:text-center">
                <div>
                  <h2 className="mb-2 font-semibold text-2xl">{template?.scene_name || title}</h2>
                  {templateLoading ? (
                    <div className="h-5 w-16 animate-pulse rounded bg-system-text05" />
                  ) : (
                    <div className="text-sm text-system-white64">
                      {`${formatCountForTemplate(template?.react_info?.use_count || 0, locale)} ${t('h5.shareContent.templateUsed')}`}
                    </div>
                  )}
                </div>

                {templateLoading ? (
                  <div className="flex gap-2 sm:order-first sm:justify-center">
                    <div className="aspect-square size-20 animate-pulse rounded-6 bg-system-text05 shadow-sm" />
                  </div>
                ) : (
                  !!portraitResource?.image?.uris?.length && (
                    <div className="flex sm:order-first sm:justify-center">
                      {portraitResource.image.uris.map((uri, idx) => (
                        <img
                          key={`${scene}-${uri.slice(uri.length - 10, uri.length)}-${idx}`}
                          src={uri}
                          alt=""
                          className={cn(
                            'aspect-square h-20 w-fit rounded-6 border border-system-white object-cover shadow-sm',
                            idx === 0 && 'rotate-[-2deg]',
                            portraitResource?.image?.uris &&
                              idx === portraitResource.image.uris.length - 1 &&
                              'rotate-[2deg]',
                          )}
                        />
                      ))}
                    </div>
                  )
                )}

                <Button
                  variant="primary"
                  className="h-11 w-full text-base"
                  onClick={() => {
                    onClickCreate();
                  }}
                >
                  {t('templates.button')}
                </Button>
              </div>

              {(onPrev || onNext) && (
                <>
                  {onPrev && (
                    <button
                      type="button"
                      onClick={onPrev}
                      disabled={hasPrev === false}
                      className="-translate-y-1/2 group absolute top-1/2 left-0 z-10 rounded-full bg-system-white04 p-3 text-system-white backdrop-blur-sm transition-opacity hover:bg-system-white20 disabled:cursor-not-allowed disabled:opacity-30 sm:hidden"
                      aria-label="Previous template"
                    >
                      <BaseLineRight className="rotate-180 text-2xl" />
                    </button>
                  )}
                  {onNext && (
                    <button
                      type="button"
                      onClick={onNext}
                      disabled={hasNext === false}
                      className="-translate-y-1/2 group absolute top-1/2 right-0 z-10 rounded-full bg-system-white04 p-3 text-system-white backdrop-blur-sm transition-opacity hover:bg-system-white20 disabled:cursor-not-allowed disabled:opacity-30 sm:hidden"
                      aria-label="Next template"
                    >
                      <BaseLineRight className="text-2xl" />
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 mb-4 flex-1 border-system-white08 border-t">
              <div className="pt-4 font-medium text-base sm:border-transparent sm:font-semibold">
                {t('templates.inspiration')}
              </div>

              <FeedList
                active={CommunityEnum.MediaAsset}
                hasMore={hasMore}
                isLoading={loading}
                handleDetail={(id, traceId) => handleDetail(id, traceId || '')}
                onLoadMore={async () => {
                  getFeedData();
                }}
                inspAssets={inspAssets}
                referenceList={[]}
                handleUnVote={unVote}
                handleVote={vote}
                columnsPreset="more"
                scrollElementId={scrollContainerId}
                hideEmptyVoteCount
                openRecreateInNewTab={false}
                onRecreateStart={onClose}
              />
            </div>
          </div>
        </div>
        <DrawerClose className="absolute top-6 right-6 rounded-8 bg-system-white04 p-1.5 hover:bg-system-white20">
          <BaseLineClose className="text-xl" />
        </DrawerClose>
      </DrawerContent>
    </Drawer>
  );
};

export default TemplateDetailDrawer;
